import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { COLORS } from './constants';
import { DashboardNavState, WeeklyDataPoint, FeatureUsagePoint, TagOption } from './types';
import { DashboardApiService } from '../../../../services/dashboard.service';
import { StatCard } from '../../../../components/groups-teams-dashboard/stat-card';
import { BreadcrumbNav } from '../../../../components/groups-teams-dashboard/breadcrumb-nav';
import { WeeklyBarChart } from '../../../../components/groups-teams-dashboard/weekly-bar-chart';
import { FeatureBarChart } from '../../../../components/groups-teams-dashboard/feature-bar-chart';
import { FullWidthProgressBar } from '../../../../components/groups-teams-dashboard/progress-bar';
import { TogglePills } from '../../../../components/groups-teams-dashboard/toggle-pills';
import { TagPicker } from '../../../../components/groups-teams-dashboard/tag-picker';
import { UsersTable } from '../../../../components/groups-teams-dashboard/users-table';
import { DashboardSkeleton } from '../../../../components/groups-teams-dashboard/dashboard-skeleton';
import { UserRow } from './types';
import { exportUsersCsv } from './export-csv';

interface Level3UsersProps {
    groupId: string;
    groupName: string;
    teamId: string;
    teamName: string;
    onNavigate: (nav: Partial<DashboardNavState>) => void;
}

export const Level3Users: React.FC<Level3UsersProps> = ({
    groupId,
    groupName,
    teamId,
    teamName,
    onNavigate,
}) => {
    const [activeFilter, setActiveFilter] = useState('all');
    const [selectedUserTags, setSelectedUserTags] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    // Data state
    const [users, setUsers] = useState<UserRow[]>([]);
    const [summary, setSummary] = useState({ active: 0, inactive: 0, activeRate: 0, total: 0 });
    const [weeklyActive, setWeeklyActive] = useState<WeeklyDataPoint[]>([]);
    const [featureUsage, setFeatureUsage] = useState<FeatureUsagePoint[]>([]);
    const [userTagOptions, setUserTagOptions] = useState<TagOption[]>([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [usersData, weekly, features, tags] = await Promise.all([
                DashboardApiService.fetchUsers(teamId, '7d'),
                DashboardApiService.fetchWeeklyActive('team', teamId),
                DashboardApiService.fetchFeatureUsage('7d', 'team', teamId),
                DashboardApiService.fetchTagOptions('user'),
            ]);

            setUsers(usersData.users);
            setSummary(usersData.summary);
            setWeeklyActive(weekly);
            setFeatureUsage(features);
            setUserTagOptions(tags);
        } catch (err) {
            console.error('Failed to fetch users data:', err);
        } finally {
            setLoading(false);
        }
    }, [teamId]);

    useEffect(() => {
        fetchData();
    }, [teamId]);

    const filteredUsers = useMemo(() => {
        let result = users;
        if (activeFilter === 'act') {
            result = result.filter((u) => u.status === 'active');
        } else if (activeFilter === 'inact') {
            result = result.filter((u) => u.status === 'inactive');
        }
        if (selectedUserTags.length > 0) {
            result = result.filter((u) =>
                selectedUserTags.some((tag) => u.tags.includes(tag))
            );
        }
        return result;
    }, [users, activeFilter, selectedUserTags]);

    const activeCount = users.filter((u) => u.status === 'active').length;
    const inactiveCount = users.filter((u) => u.status === 'inactive').length;

    const goBackToGroups = () => {
        onNavigate({
            level: 1,
            groupId: null,
            groupName: null,
            teamId: null,
            teamName: null,
        });
    };

    const goBackToTeams = () => {
        onNavigate({
            level: 2,
            teamId: null,
            teamName: null,
        });
    };

    if (loading) {
        return <DashboardSkeleton statCards={3} tableColumns={8} tableRows={8} showProgressBar showFilters={false} />;
    }

    return (
        <Box>
            {/* Breadcrumb */}
            <BreadcrumbNav
                segments={[
                    { label: 'Groups & Teams', onClick: goBackToGroups },
                    { label: groupName, onClick: goBackToTeams },
                    { label: teamName },
                ]}
            />

            {/* Page Header */}
            <Box sx={{ marginBottom: '16px' }}>
                <Box component="h1" sx={{ fontSize: '20px', fontWeight: 600, margin: 0, fontFamily: 'Poppins, sans-serif' }}>
                    {teamName}
                </Box>
                <Box sx={{ fontSize: '12px', color: COLORS.grayDark, marginTop: '2px' }}>
                    {groupName} · {summary.total} POM Users · Last 7 Days
                </Box>
            </Box>

            {/* Summary Stats */}
            <Box sx={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <StatCard value={summary.active} label="Active" valueColor={COLORS.primary} />
                <StatCard value={summary.inactive} label="Inactive" valueColor={COLORS.red} />
                <StatCard
                    value={`${summary.activeRate}%`}
                    label="Active Rate"
                />
            </Box>

            {/* Progress Bar */}
            <FullWidthProgressBar percent={summary.activeRate} />

            {/* Charts */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '14px',
                    marginBottom: '16px',
                }}
            >
                <WeeklyBarChart
                    data={weeklyActive}
                    title="Weekly Active Rate"
                    subtitle="Last 12 weeks"
                />
                <FeatureBarChart
                    data={featureUsage}
                    title="Feature Usage"
                    subtitle={`${teamName} · Last 7 days`}
                />
            </Box>

            {/* User Filters */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    flexWrap: 'wrap',
                    gap: '8px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <TogglePills
                        options={[
                            { key: 'all', label: 'All', count: users.length, countColor: 'blue' },
                            { key: 'act', label: 'Active', count: activeCount, countColor: 'blue' },
                            { key: 'inact', label: 'Inactive', count: inactiveCount, countColor: 'red' },
                        ]}
                        activeKey={activeFilter}
                        onChange={setActiveFilter}
                    />
                    <TagPicker
                        options={userTagOptions}
                        selected={selectedUserTags}
                        onChange={setSelectedUserTags}
                        placeholder="User tags…"
                        minWidth={160}
                    />
                </Box>
                <Typography
                    onClick={() => exportUsersCsv(filteredUsers, teamName)}
                    sx={{
                        fontSize: '12px',
                        color: COLORS.primary,
                        cursor: 'pointer',
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    <FileDownloadOutlinedIcon sx={{ fontSize: '14px' }} />
                    Export
                </Typography>
            </Box>

            {/* Users Table */}
            <UsersTable
                users={filteredUsers}
                totalCount={filteredUsers.length}
            />
        </Box>
    );
};
